import type {
  SlideBboxArray,
  SlideCanvasBox,
  SlideCanvasPoint,
} from '../components/SlideCanvas.types';

import { rotatePoint } from './slideRotation';

const MIN_BOX_EDGE = 4;

/**
 * Logic: Simple Clamp Utility
 */
export function clamp(value: number, min: number, max: number) {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Logic: Unique ID Generator
 */
export function createBoxId() {
  return `box-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}



export function buildBox(
  start: SlideCanvasPoint,
  end: SlideCanvasPoint,
  maxWidth: number,
  maxHeight: number,
): SlideCanvasBox {
  const minX = clamp(Math.min(start.x, end.x), 0, maxWidth);
  const minY = clamp(Math.min(start.y, end.y), 0, maxHeight);
  const maxX = clamp(Math.max(start.x, end.x), 0, maxWidth);
  const maxY = clamp(Math.max(start.y, end.y), 0, maxHeight);
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    rotation: 0,
  };
}

export function isBoxDrawable(
  box: SlideCanvasBox | null,
  minVisualSize: number = MIN_BOX_EDGE,
  scaleX: number = 1,
  scaleY: number = 1,
) {
  if (!box) return false;
  const visualWidth = box.width * scaleX;
  const visualHeight = box.height * scaleY;
  return visualWidth >= minVisualSize && visualHeight >= minVisualSize;
}

/**
 * Logic: Point Containment (Rotated)
 * Determines if a point overlaps the interior of a (possibly rotated) box.
 */
export function isPointInBox(point: SlideCanvasPoint, box: SlideCanvasBox): boolean {
  const center = {
    x: box.x + box.width / 2,
    y: box.y + box.height / 2,
  };
  // Math: Inversely rotate the point around the box center to perform 
  // an axis-aligned containment check.
  const pLocal = rotatePoint(point, center, -box.rotation);
  return rectContainsPoint(box, pLocal);
}

export function rectContainsPoint(box: SlideCanvasBox, point: SlideCanvasPoint) {
  return (
    point.x >= box.x &&
    point.x <= box.x + box.width &&
    point.y >= box.y &&
    point.y <= box.y + box.height
  );
}

export function rectToBounds(box: SlideCanvasBox): SlideBboxArray {
  return [box.x, box.y, box.x + box.width, box.y + box.height];
}

export function boundsToRect(bounds: SlideBboxArray): SlideCanvasBox {
  const [minX, minY, maxX, maxY] = bounds;
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    rotation: 0,
  };
}
